import {
    ApiHandlerDiscreteParameter,
    createHandlerChainRunner,
    markSkipOnCompleteIfOnSuccessIsSet,
} from "@insite/client-framework/HandlerCreator";
import { API_URL_CURRENT_FRAGMENT } from "@insite/client-framework/Services/ApiService";
import {
    UpdateCartLineApiParameter,
    updateCartLineWithResult as updateCartLineApi,
} from "@insite/client-framework/Services/CartService";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadCurrentCart from "@insite/client-framework/Store/Data/Carts/Handlers/LoadCurrentCart";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import { addToCartEvent, removeFromCartEvent } from "@WhiteCap/Common/Utillities/CustomTracking";

interface UpdateCartLineParameter {
    onSuccess?: () => void;
    cartLine: CartLineModel;
    shouldNotReloadPromotions?: boolean;
    shouldNotLoadFullCart?: boolean;
    previousQty?: number | null;
}

type HandlerType = ApiHandlerDiscreteParameter<UpdateCartLineParameter, UpdateCartLineApiParameter>;

export const DispatchBeginUpdateCartLine: HandlerType = props => {
    props.dispatch({
        type: "Context/BeginUpdatingCart",
    });
    props.dispatch({
        type: "Pages/Cart/BeginUpdateCartLine",
    });
};

export const PopulateApiParameter: HandlerType = props => {
    props.apiParameter = {
        ...props.parameter,
        cartId: API_URL_CURRENT_FRAGMENT,
    };
};

export const UpdateCartLine: HandlerType = async props => {
    await updateCartLineApi(props.apiParameter);
};

export const TriggerUpdateQtyEvent: HandlerType = props => {
    if (props.parameter.cartLine?.qtyOrdered && props.parameter.previousQty) {
        const orderGrandTotal = getCurrentCartState(props.getState()).value?.orderGrandTotal ?? 0;
        const itemData: {
            item_name?: string;
            item_id?: string;
            quantity?: number;
            price?: number;
            item_brand?: string;
            [key: string]: any; // Allow any other properties
        } = {
            item_name: props.parameter.cartLine?.shortDescription,
            item_id: props.parameter.cartLine?.erpNumber,
            quantity: props.parameter.cartLine?.qtyOrdered,
            price: props.parameter.cartLine?.pricing?.actualPrice,
            item_brand: props.parameter.cartLine?.brand?.name ? props.parameter.cartLine?.brand?.name : "",
        };
        props.parameter.cartLine?.qtyOrdered > props.parameter.previousQty
            ? addToCartEvent({
                event: "add_to_cart",
                ecommerce: {
                    currency: "USD",
                    value: orderGrandTotal,
                    items: [
                        {
                            ...itemData,
                            quantity: props.parameter.cartLine?.qtyOrdered - props.parameter.previousQty,
                        },
                    ],
                },
            })
            : removeFromCartEvent({
                event: "remove_from_cart",
                ecommerce: {
                    currency: "USD",
                    value: orderGrandTotal,
                    items: [
                        {
                            ...itemData,
                            quantity: props.parameter.previousQty - props.parameter.cartLine?.qtyOrdered,
                        },
                    ],
                },
            });
    }
};

export const LoadCart: HandlerType = props => {
    props.parameter.shouldNotLoadFullCart
        ? props.dispatch(
            loadCurrentCart({
                shouldLoadFullCart: false,
                getCartLines: true,
                getCostCodes: true,
                getPromotions: !props.parameter.shouldNotReloadPromotions,
            }),
        )
        : props.dispatch(loadCurrentCart({ getPromotions: !props.parameter.shouldNotReloadPromotions }));
};

/** @deprecated Not needed anymore */
export const LoadPromotions: HandlerType = () => { };

export const DispatchCompleteUpdateCartLine: HandlerType = props => {
    props.dispatch({
        type: "Pages/Cart/CompleteUpdateCartLine",
    });
    props.dispatch({
        type: "Context/CompleteUpdatingCart",
    });
};

export const ExecuteOnSuccessCallback: HandlerType = props => {
    markSkipOnCompleteIfOnSuccessIsSet(props);
    props.parameter.onSuccess?.();
};

export const chain = [
    DispatchBeginUpdateCartLine,
    PopulateApiParameter,
    UpdateCartLine,
    TriggerUpdateQtyEvent,
    LoadCart,
    LoadPromotions,
    DispatchCompleteUpdateCartLine,
    ExecuteOnSuccessCallback,
];

const updateCartLineExpanded = createHandlerChainRunner(chain, "UpdateCartLine");
export default updateCartLineExpanded;
